import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import InputSearch from '../../common/InputSearch';
import DeleteEntryModal from '../../common/DeleteEntryModal';
import { Spinner } from '../../common/Spinner/Spinner';
import ErrorResponse from '../../shared/ErrorResponse';
import styles from './Counterparties.module.scss';
import { CounterpartiesTable } from './CounterpartiesTable/CounterpartiesTable';
import { CounterpartyFormModal } from './CounterpartyFormModal';
import {
  clearCounterpartyDetails,
  deleteCounterpartyRequest,
  deleteCounterpartySuccess,
  getCounterpartiesListRequest,
  getCounterpartyDetailsRequest,
} from './services/actions';
import { ICounterparty } from './services/interfaces';
import { counterpartySelector } from './services/selector';

export const Counterparties: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    counterparties,
    loading,
    deleteCounterpartyResponse,
    getCounterpartiesErrorResponses,
    addCounterpartyErrorResponses,
    updateCounterpartyErrorResponses,
    deleteCounterpartyErrorResponses,
    getCounterpartyDetailsErrorResponses,
  } = useSelector(counterpartySelector);

  const [tableData, setTableData] = useState<ICounterparty[]>(counterparties);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [deleteTarget, setDeleteTarget] = useState<ICounterparty | null>(null);

  useEffect(() => {
    setTableData(counterparties);
  }, [counterparties]);

  useEffect(() => {
    dispatch(getCounterpartiesListRequest());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(getCounterpartiesErrorResponses).length !== 0 && getCounterpartiesErrorResponses?.status === 403) {
      navigate('/error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCounterpartiesErrorResponses]);

  useEffect(() => {
    if (deleteCounterpartyResponse) {
      dispatch(deleteCounterpartySuccess(false));
      setDeleteTarget(null);
    }
  }, [deleteCounterpartyResponse, dispatch]);

  const openCreate = () => {
    dispatch(clearCounterpartyDetails());
    setFormMode('create');
    setFormOpen(true);
  };

  const openEdit = (row: ICounterparty) => {
    dispatch(getCounterpartyDetailsRequest({ id: row.id }));
    setFormMode('edit');
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    dispatch(clearCounterpartyDetails());
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    dispatch(deleteCounterpartyRequest({ id: deleteTarget.id }));
    closeForm();
  };

  return (
    <div className={styles.wrapper}>
      {Object.keys(getCounterpartiesErrorResponses).length !== 0 ? (
        <ErrorResponse error={getCounterpartiesErrorResponses} />
      ) : null}
      {Object.keys(addCounterpartyErrorResponses).length !== 0 ? (
        <ErrorResponse error={addCounterpartyErrorResponses} />
      ) : null}
      {Object.keys(updateCounterpartyErrorResponses).length !== 0 ? (
        <ErrorResponse error={updateCounterpartyErrorResponses} />
      ) : null}
      {Object.keys(deleteCounterpartyErrorResponses).length !== 0 ? (
        <ErrorResponse error={deleteCounterpartyErrorResponses} />
      ) : null}
      {Object.keys(getCounterpartyDetailsErrorResponses).length !== 0 ? (
        <ErrorResponse error={getCounterpartyDetailsErrorResponses} />
      ) : null}

      <div className={styles.loadingGroup}>
        <span className={styles.pageTitle}>Контрагенти</span>
        {loading ? <Spinner /> : null}
      </div>
      <span>Керування контрагентами</span>

      <section className={styles.header}>
        <div className={styles.searchSection}>
          <div className={styles.searchGroup}>
            <span>Фільтр по GLN:</span>
            <InputSearch setTableData={setTableData} data={counterparties} filterFieldName="gln" />
          </div>
          <Button variant="contained" className={styles.btn} onClick={openCreate}>
            <span className={styles.btnText}>Додати</span>
            <AddIcon className={styles.icon} />
          </Button>
        </div>
      </section>

      <section>
        <CounterpartiesTable data={tableData} onEdit={openEdit} />
      </section>

      <CounterpartyFormModal
        isOpen={formOpen}
        mode={formMode}
        onClose={closeForm}
        onDeleteClick={(row) => {
          setDeleteTarget(row);
        }}
      />

      <DeleteEntryModal
        isShow={Boolean(deleteTarget)}
        title={deleteTarget ? `Видалити контрагента GLN ${deleteTarget.gln}?` : ''}
        closeModal={() => setDeleteTarget(null)}
        deleteHandler={confirmDelete}
      />
    </div>
  );
};
